import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  Filter,
  AlertCircle,
  Download,
  TrendingUp,
  PieChart,
  ChevronUpIcon,
  ChevronDownIcon,
} from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import toastService from '@/services/toast/service';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { BILLING_STATUS_CONFIG } from '@/lib/billingStatus';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { TableSkeleton } from '@/components/skeletons';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { RelativeTime } from '@/components/RelativeTime';
import { useDebounce } from '@/hooks/useDebounce';
import { generatePaginationPages } from '@/lib/pagination';
import { toDateParam, fromDateParam } from '@/lib/date-param';
import { readCampaignsView, writeCampaignsView, agencySearchFromCampaigns } from '@/lib/campaigns-url';

import { plansService, AdminPlan } from '@/services/plans';
import {
  campaignsService,
  ATTRIBUTION_DIMENSIONS,
  ATTRIBUTION_LABELS,
  type AttributionDimension,
  type CampaignFilters,
  type CampaignFlatRow,
  type CampaignGroupedRow,
} from '@/services/campaigns';

// Status badge config (matches AgenciesList for visual consistency). Takes the
// API's computed billingStatus, not the raw Stripe status: a cancelled agency has
// no Stripe status left to read.
function getStatusBadge(billingStatus: string) {
  const config = BILLING_STATUS_CONFIG[billingStatus] || BILLING_STATUS_CONFIG.free;
  return <Badge tone={config.tone}>{config.label}</Badge>;
}

function fmtCell(value: string | null | undefined): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground/60 italic text-xs">—</span>;
  }
  return <span className="text-sm">{value}</span>;
}

/** The affiliate's code, with the customer's referral ID under it */
function affiliateCell(code: string | null | undefined, referralId: string | null | undefined): React.ReactNode {
  if (!referralId) return fmtCell(code);
  return (
    <div className="leading-tight">
      {code ? <span className="text-sm">{code}</span> : <span className="text-muted-foreground/60 italic text-xs">unknown code</span>}
      <div className="text-xs text-muted-foreground font-mono">{referralId}</div>
    </div>
  );
}

function fmtPercent(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function fmtMoney(n: number): string {
  return `$${n.toLocaleString()}`;
}

function escapeCsv(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

type CsvCell = string | number | null | undefined;

function downloadCsv(filename: string, rows: CsvCell[][]) {
  const csv = rows.map((r) => r.map(escapeCsv).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

type ViewMode = 'flat' | 'grouped';

const FLAT_PAGE_SIZE = 25;
const GROUPED_PAGE_SIZE = 50;

export default function CampaignsList() {
  const navigate = useNavigate();
  // The search, filters, view, sort and page live in the URL so leaving for an
  // agency and coming back (or reloading, or sharing the link) lands on the
  // same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialView] = useState(() => readCampaignsView(searchParams));
  // Handed down to an opened agency, so its back arrow returns to this view.
  const agencySearch = agencySearchFromCampaigns(searchParams);

  // ── View mode + group dimension ────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>(initialView.viewMode);
  const [groupBy, setGroupBy] = useState<AttributionDimension>(initialView.groupBy);

  // ── Data state ─────────────────────────────────────────────────────
  const [flatRows, setFlatRows] = useState<CampaignFlatRow[]>([]);
  const [groupedRows, setGroupedRows] = useState<CampaignGroupedRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // ── Filters ────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState(initialView.search);
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [page, setPage] = useState(initialView.page);
  const [statusFilter, setStatusFilter] = useState<string>(initialView.status);
  const [planFilter, setPlanFilter] = useState<string>(initialView.planId);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const from = fromDateParam(initialView.dateFrom ?? null);
    const to = fromDateParam(initialView.dateTo ?? null);
    return from || to ? { from, to } : undefined;
  });
  const [includeUntracked, setIncludeUntracked] = useState(initialView.includeUntracked);
  const [plans, setPlans] = useState<AdminPlan[]>([]);

  // ── Sorting ────────────────────────────────────────────────────────
  const [flatSort, setFlatSort] = useState<{ by: string; dir: 'asc' | 'desc' }>(initialView.flatSort);
  const [groupedSort, setGroupedSort] = useState<{ by: string; dir: 'asc' | 'desc' }>(initialView.groupedSort);

  const hasActiveFilters = !!(
    statusFilter ||
    planFilter ||
    dateRange?.from ||
    debouncedSearch
  );

  const filters = useMemo<CampaignFilters>(
    () => ({
      status: statusFilter || undefined,
      planId: planFilter || undefined,
      dateFrom: dateRange?.from ? toDateParam(dateRange.from) : undefined,
      dateTo: dateRange?.to ? toDateParam(dateRange.to) : undefined,
      search: debouncedSearch || undefined,
      includeUntracked,
    }),
    [statusFilter, planFilter, dateRange, debouncedSearch, includeUntracked],
  );

  useEffect(() => {
    plansService
      .getPlans()
      .then(setPlans)
      .catch(() => undefined);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      if (viewMode === 'flat') {
        const res = await campaignsService.getFlat({
          ...filters,
          page,
          limit: FLAT_PAGE_SIZE,
          sortBy: flatSort.by,
          sortDir: flatSort.dir,
        });
        setFlatRows(res.data);
        setTotal(res.meta.total);
        setTotalPages(res.meta.totalPages);
      } else {
        const res = await campaignsService.getGrouped(groupBy, {
          ...filters,
          page,
          limit: GROUPED_PAGE_SIZE,
          sortBy: groupedSort.by,
          sortDir: groupedSort.dir,
        });
        setGroupedRows(res.data);
        setTotal(res.meta.total);
        setTotalPages(res.meta.totalPages);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          'Failed to load campaign data. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [viewMode, groupBy, filters, page, flatSort, groupedSort]);

  // A filter or view change goes back to page 1 before it fetches, so it
  // costs one request rather than one for the old page and one for the first.
  const resetKey = JSON.stringify([filters, viewMode, groupBy]);
  const lastResetKey = useRef(resetKey);
  useEffect(() => {
    if (lastResetKey.current !== resetKey) {
      lastResetKey.current = resetKey;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    fetchData();
  }, [fetchData, resetKey, page]);

  // Mirror the applied view back into the URL. Writing only on a real change
  // keeps the replace() out of a feedback loop with the router.
  const lastQuery = useRef(searchParams.toString());
  useEffect(() => {
    const next = writeCampaignsView({
      viewMode,
      groupBy,
      search: debouncedSearch,
      status: statusFilter,
      planId: planFilter,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      includeUntracked,
      flatSort,
      groupedSort,
      page,
    });
    const nextQuery = next.toString();
    if (nextQuery === lastQuery.current) return;
    lastQuery.current = nextQuery;
    setSearchParams(next, { replace: true });
  }, [
    viewMode, groupBy, debouncedSearch, statusFilter, planFilter, filters.dateFrom, filters.dateTo,
    includeUntracked, flatSort, groupedSort, page, setSearchParams,
  ]);

  const toggleFlatSort = (column: string) => {
    setFlatSort((prev) =>
      prev.by === column
        ? { by: column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { by: column, dir: 'asc' },
    );
  };

  const toggleGroupedSort = (column: string) => {
    setGroupedSort((prev) =>
      prev.by === column
        ? { by: column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { by: column, dir: 'desc' },
    );
  };

  const sortIcon = (sortState: { by: string; dir: 'asc' | 'desc' }, column: string) => {
    if (sortState.by !== column) {
      return (
        <span className="inline-block ml-1 text-muted-foreground/40 text-xs">
          ↕
        </span>
      );
    }
    return sortState.dir === 'asc' ? (
      <ChevronUpIcon className="h-3 w-3 inline ml-1" />
    ) : (
      <ChevronDownIcon className="h-3 w-3 inline ml-1" />
    );
  };

  // ── CSV export ─────────────────────────────────────────────────────
  const handleExport = async () => {
    setIsExporting(true);
    try {
      if (viewMode === 'flat') {
        const all = await campaignsService.exportFlatAll({
          ...filters,
          sortBy: flatSort.by,
          sortDir: flatSort.dir,
        });
        const header = [
          'Created',
          'Agency',
          'Agency slug',
          'Admin name',
          'Email',
          'Phone',
          'Plan',
          'Current plan',
          'Status',
          'Billing status',
          'Lifecycle stage',
          'Has paid before',
          'Converted',
          'Trial ends',
          'Business type',
          'Heard from',
          'Source',
          'Referrer',
          'Funnel',
          'Campaign',
          'UTM source',
          'Ad set',
          'Ad',
          'Placement',
          'Affiliate',
          'Affiliate referral ID',
        ];
        const body = all.map((r) => [
          r.createdAtUtc,
          r.agencyName,
          r.agencySlug,
          r.adminName ?? '',
          r.adminEmail ?? '',
          r.phone ?? '',
          r.planName ?? '',
          r.currentPlan ?? '',
          r.stripeStatus ?? 'free',
          r.billingStatus,
          r.lifecycleStage,
          r.hasPaidBefore ? 'yes' : 'no',
          r.converted ? 'yes' : 'no',
          r.trialEndsAtUtc ?? '',
          r.businessType ?? '',
          r.heardFrom ?? '',
          r.attribution.source ?? '',
          r.attribution.referrer ?? '',
          r.attribution.funnel_slug ?? '',
          r.attribution.utm_campaign ?? '',
          r.attribution.utm_source ?? '',
          r.attribution.adset ?? '',
          r.attribution.ad ?? '',
          r.attribution.placement ?? '',
          r.attribution.affiliateCode ?? '',
          r.attribution.affiliateReferralId ?? '',
        ]);
        downloadCsv(`campaigns-flat-${Date.now()}.csv`, [header, ...body]);
      } else {
        const all = await campaignsService.exportGroupedAll(groupBy, {
          ...filters,
          sortBy: groupedSort.by,
          sortDir: groupedSort.dir,
        });
        const byAffiliate = groupBy === 'affiliate';
        const header = [
          ATTRIBUTION_LABELS[groupBy],
          ...(byAffiliate ? ['Affiliate referral ID'] : []),
          'Signups',
          'Trialing',
          'Active paid',
          'Cancelled',
          'Converted',
          'Conversion %',
          'MRR',
        ];
        const body = all.map((r) => [
          (byAffiliate && r.affiliateReferralId ? r.affiliateCode : r.groupValue) ?? '(none)',
          ...(byAffiliate ? [r.affiliateReferralId ?? ''] : []),
          r.signups,
          r.trialing,
          r.activePaid,
          r.canceled,
          r.converted,
          fmtPercent(r.conversionRate),
          r.mrr,
        ]);
        downloadCsv(
          `campaigns-by-${groupBy}-${Date.now()}.csv`,
          [header, ...body],
        );
      }
      toastService.success('CSV downloaded');
    } catch (err: any) {
      toastService.error(err?.response?.data?.message || "Couldn't export CSV");
    } finally {
      setIsExporting(false);
    }
  };

  // Drill-down: clicking a grouped row pre-filters the flat view
  const drillDown = (row: CampaignGroupedRow) => {
    setSearchQuery(row.groupValue ?? '');
    setViewMode('flat');
  };

  // ──────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────

  const flatColumnCount = 13;
  const groupedColumnCount = 8;
  const columnCount = viewMode === 'flat' ? flatColumnCount : groupedColumnCount;

  return (
    <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-muted-foreground" />
            Campaigns{' '}
            {!isLoading && (
              <span className="text-muted-foreground font-normal">({total})</span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            See which ads, funnels and affiliates convert into paying customers.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isLoading || isExporting || total === 0}
            className="w-full sm:w-auto"
          >
            <Download className={`h-4 w-4 mr-2 ${isExporting ? 'animate-pulse' : ''}`} />
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </Button>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : (
        <div className="rounded-lg border bg-card">
          {/* Toolbar 1 — view mode + group dimension (single picker) */}
          <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between border-b">
            <div className="flex items-center gap-2 flex-wrap">
              <Label htmlFor="view-picker" className="text-sm text-muted-foreground">
                View
              </Label>
              <Select
                value={viewMode === 'flat' ? '__flat__' : groupBy}
                onValueChange={(v) => {
                  if (v === '__flat__') {
                    setViewMode('flat');
                  } else {
                    setViewMode('grouped');
                    setGroupBy(v as AttributionDimension);
                  }
                }}
              >
                <SelectTrigger
                  id="view-picker"
                  className="h-9 w-[260px] text-sm font-medium"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__flat__">
                    <span className="flex items-center gap-2">
                      <Filter className="h-4 w-4" /> All signups (no grouping)
                    </span>
                  </SelectItem>
                  {ATTRIBUTION_DIMENSIONS.map((d) => (
                    <SelectItem key={d} value={d}>
                      <span className="flex items-center gap-2">
                        <PieChart className="h-4 w-4" />
                        Group by {ATTRIBUTION_LABELS[d]}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {viewMode === 'grouped' && (
              <p className="text-xs text-muted-foreground">
                Click any row to drill down into the underlying signups
              </p>
            )}
          </div>

          {/* Toolbar 2 — search + filters */}
          <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between border-b">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search campaign, ad, funnel, affiliate…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-9${isLoading ? ' opacity-50 cursor-not-allowed' : ''}`}
                readOnly={isLoading}
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Select
                value={statusFilter || 'all'}
                onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="h-9 w-[130px] text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="paying">Active</SelectItem>
                  <SelectItem value="trialing">Trialing</SelectItem>
                  <SelectItem value="cancelling">Cancelling</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="trial_expired">Trial Expired</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="incomplete">Incomplete</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="not_selected">Not selected</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={planFilter || 'all'}
                onValueChange={(v) => setPlanFilter(v === 'all' ? '' : v)}
              >
                <SelectTrigger className="h-9 w-[120px] text-sm">
                  <SelectValue placeholder="Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All plans</SelectItem>
                  <SelectItem value="none">Not selected</SelectItem>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id.toString()}>
                      {plan.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DateRangePicker
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                className="w-[230px] text-sm"
                disabled={isLoading}
              />

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 px-2">
                    <Checkbox
                      id="include-untracked"
                      checked={includeUntracked}
                      onCheckedChange={(v) => setIncludeUntracked(v === true)}
                    />
                    <Label
                      htmlFor="include-untracked"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Include untracked
                    </Label>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  Show agencies that signed up before attribution tracking, or with no UTM params at all.
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  {viewMode === 'flat' ? (
                    <>
                      <TableHead
                        className="cursor-pointer select-none w-[120px]"
                        onClick={() => toggleFlatSort('createdAtUtc')}
                      >
                        Created {sortIcon(flatSort, 'createdAtUtc')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[180px]"
                        onClick={() => toggleFlatSort('agency')}
                      >
                        Agency {sortIcon(flatSort, 'agency')}
                      </TableHead>
                      <TableHead className="text-center w-[110px]">Plan</TableHead>
                      <TableHead className="text-center w-[110px]">Status</TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[130px]"
                        onClick={() => toggleFlatSort('source')}
                      >
                        Source {sortIcon(flatSort, 'source')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[140px]"
                        onClick={() => toggleFlatSort('referrer')}
                      >
                        Referrer {sortIcon(flatSort, 'referrer')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[120px]"
                        onClick={() => toggleFlatSort('funnel_slug')}
                      >
                        Funnel {sortIcon(flatSort, 'funnel_slug')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[170px]"
                        onClick={() => toggleFlatSort('utm_campaign')}
                      >
                        Campaign {sortIcon(flatSort, 'utm_campaign')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[110px]"
                        onClick={() => toggleFlatSort('utm_source')}
                      >
                        UTM Source {sortIcon(flatSort, 'utm_source')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[150px]"
                        onClick={() => toggleFlatSort('adset')}
                      >
                        Ad Set {sortIcon(flatSort, 'adset')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[140px]"
                        onClick={() => toggleFlatSort('ad')}
                      >
                        Ad {sortIcon(flatSort, 'ad')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[140px]"
                        onClick={() => toggleFlatSort('placement')}
                      >
                        Placement {sortIcon(flatSort, 'placement')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[120px]"
                        onClick={() => toggleFlatSort('affiliate')}
                      >
                        Affiliate {sortIcon(flatSort, 'affiliate')}
                      </TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead
                        className="cursor-pointer select-none min-w-[220px]"
                        onClick={() => toggleGroupedSort('group_value')}
                      >
                        {ATTRIBUTION_LABELS[groupBy]} {sortIcon(groupedSort, 'group_value')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-center w-[100px]"
                        onClick={() => toggleGroupedSort('signups')}
                      >
                        Signups {sortIcon(groupedSort, 'signups')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-center w-[100px]"
                        onClick={() => toggleGroupedSort('trialing')}
                      >
                        Trialing {sortIcon(groupedSort, 'trialing')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-center w-[110px]"
                        onClick={() => toggleGroupedSort('active_paid')}
                      >
                        Active paid {sortIcon(groupedSort, 'active_paid')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-center w-[110px]"
                        onClick={() => toggleGroupedSort('canceled')}
                      >
                        Cancelled {sortIcon(groupedSort, 'canceled')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-center w-[110px]"
                        onClick={() => toggleGroupedSort('converted')}
                      >
                        Converted {sortIcon(groupedSort, 'converted')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-center w-[120px]"
                        onClick={() => toggleGroupedSort('conversion_rate')}
                      >
                        Conversion {sortIcon(groupedSort, 'conversion_rate')}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-center w-[110px]"
                        onClick={() => toggleGroupedSort('mrr')}
                      >
                        MRR {sortIcon(groupedSort, 'mrr')}
                      </TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>

              <TableBody className={isLoading ? 'pointer-events-none' : ''}>
                {isLoading ? (
                  <TableSkeleton columns={columnCount} rows={6} />
                ) : (viewMode === 'flat' ? flatRows.length : groupedRows.length) === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      className="h-48 text-center text-muted-foreground"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                          {hasActiveFilters ? (
                            <Filter className="h-6 w-6 text-muted-foreground" />
                          ) : (
                            <TrendingUp className="h-6 w-6 text-muted-foreground" />
                          )}
                        </div>
                        <h3 className="text-lg font-medium mb-2">
                          {hasActiveFilters ? 'No matches' : 'No campaign data yet'}
                        </h3>
                        <p className="text-sm text-muted-foreground max-w-md">
                          {hasActiveFilters
                            ? 'No agencies match your current filters. Try clearing some.'
                            : 'Once new agencies sign up with UTM parameters or via affiliates, attribution data will appear here.'}
                        </p>
                        {!includeUntracked && !hasActiveFilters && (
                          <button
                            type="button"
                            className="mt-3 text-sm underline text-foreground"
                            onClick={() => setIncludeUntracked(true)}
                          >
                            Include agencies without tracking →
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : viewMode === 'flat' ? (
                  flatRows.map((r) => (
                    <TableRow
                      key={r.agencyId}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate({ pathname: `/agencies/${r.agencyId}`, search: agencySearch })}
                    >
                      <TableCell className="text-muted-foreground text-sm">
                        <RelativeTime date={r.createdAtUtc} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          {/* A real link, so right-click, cmd-click and middle-click open the
                              agency in a new tab. The row's own click still opens it here. */}
                          <Link
                            to={{ pathname: `/agencies/${r.agencyId}`, search: agencySearch }}
                            onClick={(e) => e.stopPropagation()}
                            className="font-medium text-sm hover:underline"
                          >
                            {r.agencyName}
                          </Link>
                          {r.adminEmail && (
                            <span className="text-xs text-muted-foreground">
                              {r.adminEmail}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {r.planName || <span className="text-muted-foreground">Not selected</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          {getStatusBadge(r.billingStatus)}
                          {r.converted && (
                            <span className="text-[10px] text-emerald-600 font-medium">
                              ✓ converted
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{fmtCell(r.attribution.source)}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={r.attribution.referrer ?? ''}>
                        {fmtCell(r.attribution.referrer)}
                      </TableCell>
                      <TableCell>{fmtCell(r.attribution.funnel_slug)}</TableCell>
                      <TableCell className="max-w-[220px] truncate" title={r.attribution.utm_campaign ?? ''}>
                        {fmtCell(r.attribution.utm_campaign)}
                      </TableCell>
                      <TableCell>{fmtCell(r.attribution.utm_source)}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={r.attribution.adset ?? ''}>
                        {fmtCell(r.attribution.adset)}
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate" title={r.attribution.ad ?? ''}>
                        {fmtCell(r.attribution.ad)}
                      </TableCell>
                      <TableCell>{fmtCell(r.attribution.placement)}</TableCell>
                      <TableCell>{affiliateCell(r.attribution.affiliateCode, r.attribution.affiliateReferralId)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  groupedRows.map((r, idx) => (
                    <TableRow
                      key={`${r.groupValue ?? '__null__'}-${idx}`}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => drillDown(r)}
                    >
                      <TableCell className="font-medium">
                        {r.affiliateReferralId ? (
                          affiliateCell(r.affiliateCode, r.affiliateReferralId)
                        ) : (
                          r.groupValue ?? (
                            <span className="text-muted-foreground/60 italic">
                              (no value)
                            </span>
                          )
                        )}
                      </TableCell>
                      <TableCell className="text-center">{r.signups}</TableCell>
                      <TableCell className="text-center text-blue-600">{r.trialing}</TableCell>
                      <TableCell className="text-center text-emerald-600 font-medium">
                        {r.activePaid}
                      </TableCell>
                      <TableCell className="text-center text-red-600">{r.canceled}</TableCell>
                      <TableCell className="text-center">{r.converted}</TableCell>
                      <TableCell className="text-center font-medium">
                        <span
                          className={
                            r.conversionRate >= 0.2
                              ? 'text-emerald-600'
                              : r.conversionRate >= 0.05
                              ? 'text-foreground'
                              : 'text-muted-foreground'
                          }
                        >
                          {fmtPercent(r.conversionRate)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {fmtMoney(r.mrr)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing{' '}
                {viewMode === 'flat' ? flatRows.length : groupedRows.length} of {total}
              </p>
              <Pagination
                className={`mx-0 w-auto ${isLoading ? 'pointer-events-none opacity-50' : ''}`}
              >
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className={
                        page === 1
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                  {generatePaginationPages(page, totalPages).map((item, i) =>
                    item === 'ellipsis' ? (
                      <PaginationItem key={`ellipsis-${i}`}>
                        <span className="px-2 text-muted-foreground">…</span>
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={item}>
                        <PaginationLink
                          onClick={() => setPage(item as number)}
                          isActive={(item as number) === page}
                          className="cursor-pointer"
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    ),
                  )}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      className={
                        page === totalPages
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
